import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, ArrowRight, Loader2, Scale } from 'lucide-react';
import { PublicNav } from '../components/layout/PublicNav';
import { contentApi } from '../services/api';

interface PageData {
  slug: string;
  page_type: string;
  title: string;
  meta_description: string;
  body_md: string;
  created_at: string;
}

/** Imperative SEO meta — the SPA has no helmet; Googlebot renders JS. */
function applyMeta(page: PageData, path: string) {
  document.title = `${page.title} | Urava`;
  const ensure = (selector: string, create: () => HTMLElement) => {
    let el = document.head.querySelector(selector) as HTMLElement | null;
    if (!el) { el = create(); document.head.appendChild(el); }
    return el;
  };
  const desc = ensure('meta[name="description"]', () => {
    const m = document.createElement('meta'); m.setAttribute('name', 'description'); return m;
  });
  desc.setAttribute('content', page.meta_description);
  const canonical = ensure('link[rel="canonical"]', () => {
    const l = document.createElement('link'); l.setAttribute('rel', 'canonical'); return l;
  });
  canonical.setAttribute('href', `https://urava.app${path}`);

  // Article structured data
  const ldId = 'content-article-ld';
  document.getElementById(ldId)?.remove();
  const ld = document.createElement('script');
  ld.type = 'application/ld+json';
  ld.id = ldId;
  ld.text = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: page.title,
    description: page.meta_description,
    datePublished: page.created_at,
    author: { '@type': 'Organization', name: 'Urava', url: 'https://urava.app' },
    publisher: { '@type': 'Organization', name: 'Urava', url: 'https://urava.app' },
  });
  document.head.appendChild(ld);
}

export function ContentArticle() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [page, setPage] = useState<PageData | null>(null);
  const [status, setStatus] = useState<'loading' | 'ok' | 'missing'>('loading');

  useEffect(() => {
    if (!slug) return;
    setStatus('loading');
    contentApi.getPage(slug)
      .then(({ data }) => { setPage(data); setStatus('ok'); applyMeta(data, location.pathname); })
      .catch(() => setStatus('missing'));
  }, [slug, location.pathname]);

  return (
    <div className="min-h-screen bg-legal-50">
      <PublicNav />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/learn" className="inline-flex items-center gap-2 text-sm text-legal-500 hover:text-legal-700 mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4" /> All articles
        </Link>

        {status === 'loading' && (
          <div className="flex justify-center py-24 text-legal-400">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}

        {status === 'missing' && (
          <div className="text-center py-24">
            <p className="font-display text-2xl text-legal-900 mb-2">Article not found.</p>
            <Link to="/learn" className="text-accent-700 underline text-sm">Browse the library</Link>
          </div>
        )}

        {status === 'ok' && page && (
          <article>
            <p className="text-[11px] uppercase tracking-[0.2em] text-accent-700 mb-3">
              {page.page_type === 'compare' ? 'Comparison' : 'Legal Research Guide'}
            </p>
            <h1 className="font-display text-3xl sm:text-4xl font-medium text-legal-900 leading-tight mb-3">
              {page.title}
            </h1>
            <p className="text-legal-500 text-sm mb-8">
              {new Date(page.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
              {' · '}Urava Research Desk
            </p>

            <div className="prose prose-slate max-w-none prose-headings:font-display prose-a:text-accent-700 bg-white rounded-2xl border border-legal-200 p-6 sm:p-10">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{page.body_md}</ReactMarkdown>
            </div>

            <p className="text-xs text-legal-400 mt-6">
              This article is legal information, not legal advice. Consult a qualified advocate
              for advice on your specific case.
            </p>

            {/* CTA */}
            <div className="mt-10 relative overflow-hidden bg-legal-900 rounded-2xl p-8 text-center">
              <div className="absolute inset-0 opacity-15"
                style={{ backgroundImage: 'radial-gradient(circle at 50% 0%, #D4AF37 0%, transparent 60%)' }} />
              <Scale className="h-8 w-8 text-accent mx-auto mb-3 relative" />
              <p className="font-display text-xl text-white relative">
                Need this researched for your specific case?
              </p>
              <p className="text-legal-400 text-sm mt-2 mb-5 relative">
                Urava drafts a full, citation-backed legal research memorandum in about 10 minutes.
                3 free memos every month.
              </p>
              <Link
                to="/register"
                className="relative inline-flex items-center gap-2 bg-accent text-legal-900 font-semibold px-6 py-3 rounded-xl hover:bg-accent-400 transition-colors"
              >
                Start free research <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </article>
        )}
      </main>
    </div>
  );
}
